import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { $ } from '../../lib/api';
import {
  PageHeader, StatsRow, StatCard, FilterBar, TableWrap, TableFooter,
  CustCell, Pill, Btn, InlineProgress, StageDots, STAGE_META, STAGES_FULL,
} from '../../components/ui';
import { useAuthStore } from '../../store/auth';

// 完整 10 阶段（规格 3.1）+ all。L0 流失客户单独 tab，避免和"在跟"客户混。
const STAGE_KEYS = STAGES_FULL.map((s) => s.key); // ['L0','L1',...,'L8']
type Tab = 'all' | (typeof STAGE_KEYS)[number];

// 默认 all 排除 L0 流失客户和 L8 服务期客户（不需要再跟进）
const ACTIVE_STAGES = STAGES_FULL.filter((s) => s.key !== 'L0' && s.key !== 'L8').map((s) => s.key);

function tabToParam(t: Tab): any {
  if (t === 'all') return { stages: ACTIVE_STAGES.join(',') };
  return { stage: t };
}

function getFlag(c: any): 's' | 'a' | 'b' | 'c' | undefined {
  const h = Number(c.healthScore ?? 0);
  if (h >= 85) return 's';
  if (h >= 70) return 'a';
  if (h >= 50) return 'b';
  if (h >= 30) return 'c';
  return undefined;
}

function lastFollowText(c: any): { text: string; tone?: 'warn' | 'danger' | 'muted'; sub?: string } {
  if (!c.lastFollowupAt) return { text: '试用期未首跟', tone: 'danger', sub: '未加微信' };
  const days = dayjs().diff(dayjs(c.lastFollowupAt), 'day');
  if (days >= 5) return { text: `${days} 天前`, tone: 'warn', sub: '客户冷却中' };
  if (days >= 2) return { text: `${days} 天前`, tone: 'warn' };
  if (days >= 1) return { text: '昨天' };
  return { text: '今天' };
}

export function FollowUpsListPage() {
  const nav = useNavigate();
  // ux4：tab/page/owner 进 URL，刷新/后退不丢
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => {
    const t = searchParams.get('tab');
    return (t === 'all' || STAGE_KEYS.includes(t as any)) ? (t as Tab) : 'all';
  });
  const [page, setPage] = useState(() => { const p = Number(searchParams.get('page')); return p >= 1 ? p : 1; });
  const pageSize = 20;
  const role = useAuthStore((s) => s.user?.role);
  const isMgrView = role !== 'sales'; // 归属人筛选仅 manager/director/admin
  const [ownerFilter, setOwnerFilter] = useState<number | null>(() => {
    const o = searchParams.get('owner');
    return o ? Number(o) : null;
  });

  // 列表态写回 URL（替换式、不堆历史中间态；默认值省略键）
  const syncUrl = (next: { tab?: Tab; owner?: number | null; page?: number }) => {
    const sp = new URLSearchParams(searchParams);
    const t = next.tab ?? tab;
    const o = next.owner !== undefined ? next.owner : ownerFilter;
    const pg = next.page ?? page;
    t === 'all' ? sp.delete('tab') : sp.set('tab', t);
    o == null ? sp.delete('owner') : sp.set('owner', String(o));
    pg <= 1 ? sp.delete('page') : sp.set('page', String(pg));
    setSearchParams(sp, { replace: true });
  };

  const params = useMemo(
    () => ({ page, pageSize, ...tabToParam(tab), ...(ownerFilter ? { ownerId: ownerFilter } : {}) }),
    [page, tab, pageSize, ownerFilter],
  );

  const { data, isLoading } = useQuery({
    queryKey: ['customers-followups', params],
    queryFn: () => $.get<{ items: any[]; total: number }>('/customers', params),
  });
  // 归属人筛选下拉数据源（manager/director/admin 有 user:view）
  const { data: ownerUsers } = useQuery({
    queryKey: ['owner-filter-users'],
    enabled: isMgrView,
    queryFn: () => $.get<{ items: any[] }>('/users', { pageSize: 200 }),
    staleTime: 300_000,
  });

  // 每个 stage 拉 1 条只为拿 total — 11 个并发请求（10 阶段 + all）
  const { data: summary } = useQuery({
    queryKey: ['followups-summary'],
    queryFn: async () => {
      const results = await Promise.all([
        $.get<any>('/customers', { page: 1, pageSize: 1, stages: ACTIVE_STAGES.join(',') }),
        ...STAGE_KEYS.map((s) => $.get<any>('/customers', { page: 1, pageSize: 1, stage: s })),
      ]);
      const counts: Record<string, number> = { all: results[0].total };
      STAGE_KEYS.forEach((s, i) => (counts[s] = results[i + 1].total));
      return counts;
    },
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const urgent = items.filter((c) => !c.lastFollowupAt || dayjs().diff(dayjs(c.lastFollowupAt), 'day') >= 5).length;
  const healthAvg = items.length ? Math.round(items.reduce((s, c) => s + Number(c.healthScore ?? 0), 0) / items.length) : 0;

  return (
    <>
      <PageHeader
        breadcrumb={['客户流程', '跟进记录']}
        title="跟进记录"
        tag="第 2 步"
        subtitle="把陌生人变成愿意听你说话的人"
        actions={
          <>
            <Btn kind="text">导出</Btn>
            <Btn kind="primary" onClick={() => alert('请由主管在团队页面分配客户')}>新增客户</Btn>
          </>
        }
      />
      <div className="work-content">
        <StatsRow>
          <StatCard label="在跟客户总数" value={summary?.all ?? '—'} unit="人" meta="L1–L7 在跟（不含流失/服务期）" />
          <StatCard label="需立即处理" value={urgent} unit="人" valueColor="danger" meta="5 天未跟 / 试用未首跟" />
          <StatCard label="本月新增跟进" value={total} unit="条" valueColor="success" meta="当前 tab 数量" />
          <StatCard label="客户健康度均值" value={healthAvg} unit="/100" meta="当前页样本" />
        </StatsRow>

        <FilterBar
          active={tab}
          onChange={(k) => { setTab(k as Tab); setPage(1); syncUrl({ tab: k as Tab, page: 1 }); }}
          tabs={[
            { key: 'all', label: '在跟', count: summary?.all },
            ...STAGES_FULL.map((s) => ({
              key: s.key,
              label: s.label,
              count: summary?.[s.key] ?? 0,
            })),
          ]}
        />
        {isMgrView && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>归属人：</span>
            <select
              value={ownerFilter ?? ''}
              onChange={(e) => { const v = e.target.value ? Number(e.target.value) : null; setOwnerFilter(v); setPage(1); syncUrl({ owner: v, page: 1 }); }}
              style={{ padding: '4px 8px', border: '1px solid var(--border)', borderRadius: 6, fontSize: 13 }}
            >
              <option value="">全部归属人</option>
              {(ownerUsers?.items ?? []).map((u: any) => (
                <option key={u.id} value={u.id}>{u.realName ?? u.username ?? `#${u.id}`}</option>
              ))}
            </select>
          </div>
        )}
        <TableWrap>
          <table className="ck-table">
            <thead>
              <tr>
                <th style={{ width: '24%' }}>客户</th>
                <th style={{ width: '9%' }}>来源</th>
                <th style={{ width: '9%' }}>归属人</th>
                <th style={{ width: '11%' }}>阶段</th>
                <th style={{ width: '16%' }}>健康度</th>
                <th style={{ width: '16%' }}>最后跟进</th>
                <th style={{ width: '12%' }}>下次沟通</th>
                <th style={{ width: '12%', textAlign: 'right' }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>加载中…</td></tr>
              )}
              {!isLoading && items.length === 0 && (
                <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: 'var(--text-3)' }}>暂无跟进中的客户</td></tr>
              )}
              {items.map((c: any) => {
                const stage = STAGE_META[c.currentStage] ?? { tone: 'default', label: c.currentStage };
                const last = lastFollowText(c);
                return (
                  <tr key={c.id} onClick={() => nav(`/customers/${c.id}`)}>
                    <td>
                      <CustCell
                        id={c.id}
                        name={c.nickname}
                        flag={getFlag(c)}
                        meta={c.internalCode || '—'}
                      />
                    </td>
                    <td><span className="time-text">{c.source ?? '—'}</span></td>
                    <td><span className="time-text">{c.ownerName ?? '—'}</span></td>
                    <td>
                      <Pill tone={stage.tone}>{stage.label}</Pill>
                      <StageDots current={c.currentStage} />
                    </td>
                    <td><InlineProgress value={c.healthScore ?? 0} /></td>
                    <td>
                      <div className={`time-text ${last.tone === 'danger' ? 'danger' : last.tone === 'warn' ? 'warn' : ''}`}>{last.text}</div>
                      {last.sub && <div className="time-sub">{last.sub}</div>}
                    </td>
                    <td>
                      <span className="time-text muted">未设置</span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div className="row-actions">
                        <button className="row-action" onClick={(e) => { e.stopPropagation(); nav(`/follow-ups/new?customer=${c.id}`); }}>立即跟进</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {total > 0 && (
            <TableFooter total={total} page={page} pageSize={pageSize} onPage={(p) => { setPage(p); syncUrl({ page: p }); }} />
          )}
        </TableWrap>
      </div>
    </>
  );
}
